// Bir setin erişim editörü (portal yetki v2 §8.1) — portal › Kullanıcılar çekmecesi ve danışman ›
// KPI erişimi editörü AYNI alt bileşenleri kullanır; fark yalnız `scope` (kısıtlı Admin kilitleri;
// danışman/Süper Admin'de `null` → kilit yok). Sıra: set kutusu + rol → özet → "Tüm kategoriler" →
// kategori tablosu → kırılım gizleme onay satırları (DR10) → "Gösterge istisnaları (n)" → "İşletmeler".

import type {
  ClientAdminScope,
  ClientAdminSet,
  ClientEntity,
  ClientUserSetAccess,
} from "@mahir/shared";
import { CLIENT_ADMIN_COPY } from "../../lib/client-admin-copy";
import {
  accessLocks,
  accessSummaryV2,
  categoryLevelOf,
  disableAllCategories,
  leafEntityIds,
  minLevel,
  setCategoryLevel,
  type SetDraftV2,
} from "../../lib/client-access-draft-v2";
import { totalHiddenByMember } from "../../lib/client-access-breakdown";
import { visibleCategories } from "../../lib/client-kpi-access-ui";
import { KpiSetRoleSelect } from "../kpi/KpiSetRoleSelect";
import { AccessBreakdownWarnings } from "./AccessBreakdownWarnings";
import { AccessCategoryRadios } from "./AccessCategoryRadios";
import { AccessEntitiesSection } from "./AccessEntitiesSection";
import { AccessOverridesSection } from "./AccessOverridesSection";

const C = CLIENT_ADMIN_COPY.drawer;

interface Props {
  set: ClientAdminSet;
  draft: SetDraftV2 | undefined;
  /** Sunucudan okunan satır (kilit ve "kapsam dışı" sayıları için); yeni sette yok. */
  original: ClientUserSetAccess | undefined;
  entities: ClientEntity[];
  /** Kısıtlı Admin kapsamı; Süper Admin / tam Admin / danışman için `null`. */
  scope: ClientAdminScope | null;
  disabled: boolean;
  /** Kendi rolünü denetçiye çeviren yönetici için uyarı. */
  selfAuditorNote: boolean;
  onToggle: (on: boolean) => void;
  onPatch: (patch: (s: SetDraftV2) => SetDraftV2) => void;
}

export function AccessSetEditor(props: Props): JSX.Element {
  const { set, draft, original, entities, scope, disabled } = props;
  const locks = accessLocks(original, scope);
  const cats = visibleCategories(set);
  const entityTotal = leafEntityIds(entities).length;
  const noteStyle = {
    background: "var(--color-warning-bg)",
    color: "var(--color-warning-text)",
    border: "1px solid var(--color-warning-border)",
  } as const;

  return (
    <div data-testid="access-set-editor" className="py-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft !== undefined}
            disabled={disabled || original?.outOfScope === true}
            onChange={(e) => props.onToggle(e.target.checked)}
          />
          <span style={{ fontWeight: 600 }}>{set.name}</span>
        </label>
        {draft ? (
          <KpiSetRoleSelect
            value={draft.role}
            ariaLabel={C.roleAria(set.name)}
            disabled={disabled || locks.lockedRole}
            onChange={(r) => props.onPatch((s) => ({ ...s, role: r }))}
          />
        ) : null}
      </div>
      {original?.outOfScope === true ? (
        <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
          {C.scopeLocked.setOutOfScope}
        </p>
      ) : null}

      {draft ? (
        <div
          className="mt-2 ml-1 pl-3 flex flex-col gap-3"
          style={{ borderLeft: "2px solid var(--color-border)" }}
        >
          <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
            {locks.lockedRole ? C.roleLocked : C.roleHelp}
          </p>
          {props.selfAuditorNote && draft.role === "auditor" ? (
            <p role="note" className="rounded-md px-3 py-2 text-xs" style={noteStyle}>
              {C.roleSelfNote}
            </p>
          ) : null}
          <p
            className="text-xs"
            aria-label={C.setSummaryAria(set.name)}
            style={{ color: "var(--color-text-2)", fontWeight: 600 }}
          >
            {accessSummaryV2(draft, set, entityTotal)}
          </p>

          <section>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.allCategories || locks.lockedNullCategories}
                disabled={disabled || locks.noAllCategories}
                onChange={(e) =>
                  props.onPatch((s) =>
                    e.target.checked ? { ...s, allCategories: true } : disableAllCategories(set, s),
                  )
                }
              />
              {C.categories.allToggle}
            </label>
            {locks.lockedNullCategories ? (
              <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
                {C.scopeLocked.full}
              </p>
            ) : null}
            {!draft.allCategories && !locks.lockedNullCategories ? (
              <div className="mt-1 flex flex-col">
                {cats.length === 0 ? (
                  <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
                    {C.categories.noneOpen}
                  </p>
                ) : null}
                {cats.map((c) => {
                  const stored = categoryLevelOf(set, draft, c.indicatorId);
                  let shown = minLevel(stored, c.consultantAccess);
                  if (draft.role === "auditor" && shown === "open") shown = "readonly";
                  // En yakın üst kategori açıkken "Gizli" ifade edilemez (harita 'closed' bilmez).
                  const parent = set.indicators.find((i) => i.id === c.indicatorId)?.parentId;
                  const parentOpen =
                    parent != null && categoryLevelOf(set, draft, parent) !== "closed";
                  return (
                    <AccessCategoryRadios
                      key={c.indicatorId}
                      name={`cat-${set.id}-${c.metricId}`}
                      legend={c.label}
                      hint={
                        totalHiddenByMember(set, draft, c.indicatorId)
                          ? C.totalHiddenHint
                          : undefined
                      }
                      depth={c.depth}
                      value={shown}
                      disabled={disabled}
                      disabledLevels={{
                        ...(c.consultantAccess !== "open"
                          ? { open: C.categories.consultantReadonly }
                          : draft.role === "auditor"
                            ? { open: C.categories.auditorReadonly }
                            : {}),
                        ...(parentOpen ? { closed: C.categories.parentOpen } : {}),
                      }}
                      onChange={(lv) => props.onPatch((s) => setCategoryLevel(s, c.metricId, lv))}
                    />
                  );
                })}
              </div>
            ) : null}
            {(original?.hiddenCategoryCount ?? 0) > 0 ? (
              <p className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
                {C.scopeLocked.hidden(original?.hiddenCategoryCount ?? 0)}
              </p>
            ) : null}
          </section>

          <AccessBreakdownWarnings
            set={set}
            draft={draft}
            disabled={disabled}
            onChange={(next) => props.onPatch(() => next)}
          />
          <AccessOverridesSection
            set={set}
            draft={draft}
            disabled={disabled}
            onChange={(next) => props.onPatch(() => next)}
          />
          <AccessEntitiesSection
            setId={set.id}
            entities={entities}
            hiddenCount={original?.hiddenEntityCount ?? 0}
            draft={draft}
            disabled={disabled}
            lockedNull={locks.lockedNullEntities}
            noAll={locks.noAllEntities}
            onChange={(next) => props.onPatch(() => next)}
          />
        </div>
      ) : null}
    </div>
  );
}
